import { Button, MonoLabel, cn } from '@/design-system';
import type { PaginationMeta } from '@/shared/api/envelope';
import { formatNumber } from '@/shared/lib';

export interface PagerProps {
    meta: PaginationMeta | null;
    onPage: (page: number) => void;
    /** "keuringen", "facturen": het meervoud in de telling. */
    noun: string;
    /** Een volgende pagina laadt; de knoppen wachten even. */
    busy?: boolean;
    className?: string;
}

/**
 * Bladeren door een gepagineerde lijst, met de telling uit `meta` van de API.
 * Eén pagina of minder: alleen de telling, geen knoppen die niets doen.
 */
export function Pager({ meta, onPage, noun, busy = false, className }: PagerProps) {
    if (meta === null) {
        return null;
    }

    const first = meta.total === 0 ? 0 : (meta.current_page - 1) * meta.per_page + 1;
    const last = Math.min(meta.current_page * meta.per_page, meta.total);

    return (
        <nav
            aria-label={`Pagina's ${noun}`}
            className={cn(
                'flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-[10px]',
                className,
            )}
        >
            <MonoLabel size={11} variant="value" tone="muted">
                {meta.total === 0
                    ? `0 ${noun}`
                    : `${formatNumber(first)}–${formatNumber(last)} van ${formatNumber(meta.total)} ${noun}`}
            </MonoLabel>

            {meta.last_page > 1 ? (
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="xs"
                        disabled={busy || meta.current_page <= 1}
                        onClick={() => onPage(meta.current_page - 1)}
                    >
                        Vorige
                    </Button>
                    <MonoLabel size={11} variant="value" tone="muted">
                        {`${meta.current_page} / ${meta.last_page}`}
                    </MonoLabel>
                    <Button
                        variant="outline"
                        size="xs"
                        disabled={busy || meta.current_page >= meta.last_page}
                        onClick={() => onPage(meta.current_page + 1)}
                    >
                        Volgende
                    </Button>
                </div>
            ) : null}
        </nav>
    );
}
